import currency from 'currency.js';
export class Money {
    static precision = 8;
    static currency = 'USD';
    providerValue;
    constructor(providerValue) {
        this.providerValue = providerValue;
    }
    static get currencySymbol() {
        if (Money.currency === 'USD')
            return '$';
        else if (Money.currency === 'EUR')
            return '€';
        else if (Money.currency === 'RSD')
            return 'Din';
        else if (Money.currency === 'BAM')
            return 'KM';
        return Money.currency;
    }
    static get options() {
        return {
            symbol: this.currencySymbol,
            pattern: this.currencySymbol === '$' ? '! #' : '# !',
            separator: ',',
            decimal: '.',
        };
    }
    static setCurrency(currency) {
        this.currency = currency;
    }
    static parse(value, precision = Money.precision) {
        value = Money.round(value, precision);
        const providerValue = currency(value, {
            ...Money.options,
            precision: precision,
        });
        return new Money(providerValue);
    }
    // All arithmetic is IMMUTABLE: each method returns a new Money and never mutates
    // the receiver (converted 2026-07-02 after a full call-site audit — see
    // docs/TestingStrategy.md A1; call-site behavior verified unchanged).
    add(value) {
        return new Money(this.providerValue.add(value.providerValue));
    }
    subtract(value) {
        return new Money(this.providerValue.subtract(value.providerValue));
    }
    multiply(value) {
        return new Money(this.providerValue.multiply(value));
    }
    divide(value) {
        return new Money(this.providerValue.divide(value));
    }
    percentage(value) {
        return new Money(this.providerValue.multiply(value).divide(100));
    }
    abs(setAbs = true) {
        if (setAbs && this.providerValue.value < 0) {
            return new Money(this.providerValue.multiply(-1));
        }
        return this;
    }
    toUnit() {
        return this.providerValue.value;
    }
    toFormat(format) {
        let precision = 2;
        const match = format?.match(/0\.0*/);
        if (match) {
            precision = match[0].length - 2;
        }
        else if (/^([^0]*0[^0]*)$/.test(format || '') || format?.includes('0,0')) {
            precision = 0;
        }
        const separator = !format || format?.includes('0,0') ? Money.options.separator : '';
        const symbol = format?.includes('USD') ? Money.currency : Money.options.symbol;
        const symbolRepresentation = format?.includes('USD') ? 'USD' : '$';
        let pattern = Money.options.pattern;
        if (format && !format.includes(symbolRepresentation))
            pattern = '#';
        else if (format?.startsWith(symbolRepresentation + ' '))
            pattern = '! #';
        else if (format?.startsWith(symbolRepresentation))
            pattern = '!#';
        else if (format?.endsWith(' ' + symbolRepresentation))
            pattern = '# !';
        else if (format?.endsWith(symbolRepresentation))
            pattern = '#!';
        const negativePattern = '-' + pattern;
        const options = { ...Money.options, precision, pattern, negativePattern, separator, symbol };
        const value = Money.round(this.toUnit(), precision);
        return currency(value, options).format();
    }
    toRoundedUnit(digits) {
        return Money.round(this.toUnit(), digits);
    }
    isZero() {
        return this.providerValue.value === 0;
    }
    static round(value, precision = Money.precision) {
        const opts = { precision };
        // Convert to string to handle safely and preserve original digits
        let inputStr = typeof value === 'number' ? value.toString() : value;
        // Trim whitespace for robustness (optional but good for user inputs)
        inputStr = inputStr.trim();
        // Determine if negative (check for leading '-', ignoring signs in scientific notation or similar)
        const isNegative = inputStr.startsWith('-');
        // Get absolute string by removing leading '-'
        const absStr = isNegative ? inputStr.slice(1) : inputStr;
        let roundedAbs = currency(absStr, opts).value;
        // Apply sign
        return isNegative ? -roundedAbs : roundedAbs;
    }
}
export default Money;
//# sourceMappingURL=Money.js.map